import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Package, DollarSign, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { productAPI } from '@/lib/api';

const Marketplace = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory, searchQuery]);

  const fetchCategories = async () => {
    try {
      const response = await productAPI.getCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCategory && selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await productAPI.listProducts(params);
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProducts();
  };

  return (
    <div className="min-h-screen bg-surface">
      <header className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/servpac-logo.png" alt="ServPac" className="w-8 h-8 object-contain" />
              <h1 className="text-2xl font-chivo font-bold text-slate-900">ServPac</h1>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/buyer/dashboard')}
              >
                My Dashboard
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-chivo font-bold text-slate-900 mb-2">Marketplace</h2>
          <p className="text-slate-600">Discover products from sellers worldwide</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="md:col-span-3">
            <form data-testid="search-form" onSubmit={handleSearch} className="flex gap-2">
              <Input
                data-testid="search-input"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Button data-testid="search-btn" type="submit">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </form>
          </div>

          <div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-600">Loading products...</p>
          </div>
        ) : products.length === 0 ? (
          <Card className="p-12 text-center">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-chivo font-semibold text-slate-900 mb-2">
              No products found
            </h3>
            <p className="text-slate-600">
              {searchQuery || selectedCategory !== 'all'
                ? 'Try adjusting your filters'
                : 'Be the first to list a product!'}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {products.map((product) => (
              <Card
                key={product.id}
                data-testid="product-card"
                className="overflow-hidden hover:shadow-lg transition-shadow duration-200 cursor-pointer"
                onClick={() => navigate(`/marketplace/product/${product.id}`)}
              >
                <div className="h-48 bg-slate-100 flex items-center justify-center">
                  {product.images && product.images.length > 0 ? (
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Package className="w-16 h-16 text-slate-300" />
                  )}
                </div>
                <div className="p-6">
                  <div className="mb-2">
                    <span className="text-xs px-2 py-1 bg-slate-100 text-slate-700 rounded-sm">
                      {categories.find((c) => c.id === product.category)?.name || product.category}
                    </span>
                  </div>
                  <h3 className="text-lg font-chivo font-semibold text-slate-900 mb-2">
                    {product.name}
                  </h3>
                  <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                    {product.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-accent">
                      <DollarSign className="w-5 h-5" />
                      <span className="text-2xl font-chivo font-bold">
                        {product.price_usd}
                      </span>
                      <span className="text-sm text-slate-600 ml-1">/{product.unit}</span>
                    </div>
                  </div>
                  {/* Seller Country */}
                  {product.seller_country && (
                    <div className="flex items-center text-sm text-slate-500 mt-3 pt-3 border-t border-slate-100">
                      <MapPin className="w-4 h-4 mr-1 text-ocean-deep" />
                      <span>Seller from <strong className="text-ocean-deep">{product.seller_country}</strong></span>
                    </div>
                  )}
                  {product.origin_location && (
                    <p className="text-xs text-slate-500 mt-2">
                      Product Origin: {product.origin_location}
                    </p>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Marketplace;
